import React, { useState, useRef, useEffect } from 'react';
import '../css/FnnToolView.css';
import { DetectorObject } from '../App';

// Icons
import { FaLocationDot } from "react-icons/fa6";
import { BiSolidMemoryCard } from "react-icons/bi";
import { MdOutlineDataThresholding } from "react-icons/md";
import { GiLightningFrequency } from "react-icons/gi";
import { IoIosSend } from "react-icons/io";
import { AiOutlineAim } from "react-icons/ai";

// Toast imports
import { toast } from 'react-toastify'; // Import ToastContainer
import 'react-toastify/dist/ReactToastify.css';

interface ToolProps {
    detectorData: DetectorObject[];
    server: string;
}

const getColorForMemory = (count: number, isDetectorOn: boolean) => {
    if (!isDetectorOn) return
    if (count > 15) return "red"; //red
    if (count > 0.5 && count <= 15) return "#d5b60a"; //yellow
    if (count <= 0.5) return;
};

const FnnToolView: React.FC<ToolProps> = ({ detectorData, server }) => {
    const [selectedDetector, setSelectedDetector] = useState<string | null>(null);
    const selectedCardRef = useRef<HTMLDivElement | null>(null);
    const [commandResults, setCommandResults] = useState<{ [key: string]: string }>({});
    const [threshold, setThreshold] = useState('');
    const [memory, setMemory] = useState('');

    const handleSetThreshold = (detectorIP: string) => {
        const thresholdValue = parseFloat(threshold);  // Use parseFloat to allow for any input

        // Check if it's a valid integer and within the range
        if (!Number.isInteger(thresholdValue) || thresholdValue < 5 || thresholdValue > 2047) {
            displayNotification(false, "Threshold must be an integer between 5 and 2047.");
            return;
        }

        // Perform the SET_THR request
        displayCommand(detectorIP, "SET_THR", threshold);

        // Fetch the updated GET_THR value after setting the threshold, skipping notification
        setTimeout(() => displayCommand(detectorIP, "GET_THR", '', true), 500); // Wait for the set command to complete
    };

    const handleSetMemory = (detectorIP: string) => {
        const memoryValue = parseFloat(memory);

        // Check if it's a valid integer and within the range
        if (!Number.isInteger(memoryValue) || memoryValue < 1 || memoryValue > 99) {
            displayNotification(false, "Memory must be an integer between 1 and 99.");
            return;
        }

        // Perform the SET_MEM request
        displayCommand(detectorIP, "SET_MEM", memory);

        // Fetch the updated GET_MEM value after setting the memory, skipping notification
        setTimeout(() => displayCommand(detectorIP, "GET_MEM", '', true), 500); // Wait for the set command to complete


    };

    function unselectDetector() {
        setSelectedDetector(null)
        setCommandResults({})
        setThreshold('')
        setMemory('')
    }

    function displayNotification(successOrFail: boolean, message: string) {
        // Show error toast
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#2d2f31',
                    color: 'white',
                    fontSize: '16px',
                }
            });
        }
        else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#2d2f31',
                    color: 'white',
                    fontSize: '16px',
                }
            });
        }
    }

    const displayCommand = async (detectorIP: string, command: string, value = '', skipNotification = false) => {
        try {
            // Construct URL with optional value parameter for SET_THR and SET_MEM
            const url = new URL(`/apidet/fnnbackend/fnnToolView/`, window.location.origin);
            url.searchParams.append("ip", detectorIP);
            url.searchParams.append("command", command);
            url.searchParams.append("origin", server);

            // Append value if it's a SET command
            if (command.startsWith("SET")) {
                url.searchParams.append("value", value);
            }

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: 'same-origin',  // Or 'include' based on your need
            });


            if (!response.ok) throw new Error("Failed to fetch data");

            const data = await response.json();
            const formattedResult = data.result.includes(":") ? data.result.split(":")[1].trim() : data.result;

            setCommandResults(prev => ({ ...prev, [command]: formattedResult || "No data received" }));

            // Automatically update threshold/memory fields when fetching them
            if (command === "GET_THR") setThreshold(formattedResult || '');
            if (command === "GET_MEM") setMemory(formattedResult || '');

            // If skipNotification is not true, perform notifications and clipboard actions
            if (!skipNotification) {
                // Copy result to clipboard if it’s GET_MEM
                if (command === "GET_MEM") {
                    await navigator.clipboard.writeText(formattedResult + "%");
                    displayNotification(true, `"${formattedResult}%" was copied to your clipboard.`);
                }
                else if (command.startsWith("SET")) {
                    displayNotification(true, `Successfully changed ${command.slice(-3)} to " ${formattedResult}"`);
                }
                else {
                    await navigator.clipboard.writeText(formattedResult);
                    displayNotification(true, ('"' + formattedResult + '"' + " was copied to your clipboard."));
                }
            }

        } catch (error) {
            console.error("Error fetching command:", error);
            displayNotification(false, "There was an issue completing the request.");
        }
    };

    useEffect(() => {
        // Close the selected detector if it becomes offline
        if (selectedDetector) {
            const currentDetector = Object.values(detectorData).find(detector => detector.mac === selectedDetector);
            if (currentDetector && !currentDetector.online) {
                unselectDetector();  // Close the selected card
                displayNotification(false, `${currentDetector.mac} is offline, card closed.`);
            }
        }
    }, [detectorData, selectedDetector]);


    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);

    useEffect(() => {
        if (selectedDetector && selectedCardRef.current) {
            selectedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [selectedDetector]);


    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 1370);

    useEffect(() => {
        const handleResize = () => {
            setIsWideScreen(window.innerWidth >= 1370);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    return (
        <div className="tool-wrapper">
            <div className="tool-card-stack">
                <div className="detector-column">
                    <h1 style={{ fontSize: '1.5rem' }}>🟢 Online</h1>
                    {Object.values(detectorData)
                        .filter(detector => detector?.online)
                        .sort((a, b) => {
                            const aName = a.status.site_name || 'zzz';
                            const bName = b.status.site_name || 'zzz';
                            return aName.localeCompare(bName);
                        })
                        .map(detector => (
                            <div key={detector.mac} className="tool-card-wrapper" >
                                {/* Main card */}
                                <div
                                    className="initial-tool-card"
                                    onClick={() => {
                                        if (selectedDetector === detector.mac) {
                                            setSelectedDetector(null);
                                            unselectDetector(); // unselect when clicking the same one
                                        } else {
                                            unselectDetector(); // unselect the previously selected detector
                                            setSelectedDetector(detector.mac); // select the new one
                                        }
                                    }}

                                    style={{ zIndex: "3" }}
                                >
                                    <div className="tool-card-overlay"></div>
                                    <div className='initial-tool-card-content'>
                                        <h1 style={{ zIndex: "2", marginBottom: "0rem", marginLeft: '1rem' }}>
                                            {detector.status.site_name || detector.mac}
                                        </h1>
                                        <div className='each-tool'>
                                            <h1 style={{ zIndex: "2", marginBottom: "0rem" }}>Threshhold</h1>
                                            <h1 style={{ zIndex: "2", marginBottom: "0rem" }}>{detector.status.thr}</h1>
                                        </div>
                                        <div className='each-tool'>
                                            <h1 style={{ zIndex: "2", marginBottom: "0rem" }}>Used Memory</h1>
                                            <h1 style={{
                                                zIndex: "2",
                                                marginBottom: "0rem",
                                                color: getColorForMemory(Number(detector.status.memory), detector.online),
                                            }}>
                                                {detector.status.memory}
                                            </h1>
                                        </div>
                                    </div>
                                </div>

                                {/* Dropdown under selected detector */}
                                {selectedDetector === detector.mac && (
                                    <div className="dropdown-tool-card">
                                        <div className='tool-command-and-button'>
                                            <div className='get-tools'>
                                                <div className='wrappy-and-result'>
                                                    <div className='wrappy' onClick={() => displayCommand(detector.ip, "GET_LOC")}>
                                                        <FaLocationDot className="toolIcons" />Location
                                                    </div>
                                                    {commandResults["GET_LOC"] &&
                                                        commandResults["GET_LOC"].split(",").map((value, index) => (
                                                            <React.Fragment key={index}>
                                                                {value}
                                                                <br />
                                                            </React.Fragment>
                                                        ))}


                                                </div>

                                                <div className="wrappy-and-result">
                                                    <div
                                                        className="wrappy"
                                                        onClick={() => displayCommand(detector.ip, "GET_DAC")}
                                                    >
                                                        <GiLightningFrequency className="toolIcons" />
                                                        DAC
                                                    </div>
                                                    <div className="responsive-line-break">
                                                        {commandResults["GET_DAC"] &&
                                                            commandResults["GET_DAC"].split(",").map((value, index, arr) => (
                                                                <React.Fragment key={index}>
                                                                    {value}
                                                                    {index !== arr.length - 1 && (isWideScreen ? <br /> : " ")}
                                                                </React.Fragment>
                                                            ))}
                                                    </div>
                                                </div>

                                                <div className='wrappy-and-result'>
                                                    <div className='wrappy' onClick={() => displayCommand(detector.ip, "GET_THR")}>
                                                        <MdOutlineDataThresholding className="toolIcons" />Threshold
                                                    </div>
                                                    <p>{commandResults["GET_THR"]}</p>
                                                </div>

                                                <div className='wrappy-and-result'>
                                                    <div className='wrappy' onClick={() => displayCommand(detector.ip, "GET_MEM")}>
                                                        <BiSolidMemoryCard className="toolIcons" />Memory
                                                    </div>
                                                    <p>{commandResults["GET_MEM"]}{commandResults["GET_MEM"] ? "%" : ""}</p>
                                                </div>

                                                <div className='wrappy-and-result'>
                                                    <div className='wrappy' onClick={() => displayCommand(detector.ip, "SCOPE")}>
                                                        <AiOutlineAim className="toolIcons" />Scope
                                                    </div>
                                                    {commandResults["SCOPE"] && (() => {
                                                        const words = commandResults["SCOPE"].split(" ");
                                                        const mid = Math.floor(words.length / 2); // changed from Math.ceil to Math.floor
                                                        const firstHalf = words.slice(0, mid).join(" ");
                                                        const secondHalf = words.slice(mid).join(" ");
                                                        return (
                                                            <p>
                                                                {firstHalf}
                                                                <br />
                                                                {secondHalf}
                                                            </p>
                                                        );
                                                    })()}


                                                </div>
                                            </div>

                                            <div className='set-tools'>
                                                <div className='tool-label-and-input'>
                                                    <label> Set Threshold: </label>
                                                    <div className='inner-tool-label-and-input'>
                                                        <input
                                                            className='tool-notes-input'
                                                            type="text"
                                                            name='tool-thr'
                                                            placeholder='Enter Threshold'
                                                            value={threshold}
                                                            onChange={(e) => setThreshold(e.target.value)}
                                                        />
                                                        <IoIosSend
                                                            className="toolIconsSend"
                                                            onClick={() => handleSetThreshold(detector.ip)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className='tool-label-and-input'>
                                                    <label> Set Memory %:</label>
                                                    <div className='inner-tool-label-and-input'>
                                                        <input
                                                            className='tool-notes-input'
                                                            type="text"
                                                            name='tool-mem'
                                                            placeholder='Enter Memory'
                                                            value={memory}
                                                            onChange={(e) => setMemory(e.target.value)}
                                                        />
                                                        <IoIosSend
                                                            className="toolIconsSend"
                                                            onClick={() => handleSetMemory(detector.ip)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                )}
                            </div>
                        ))}
                </div>

                <div className="detector-column">
                    <h1 style={{ fontSize: '1.5rem' }}>🔴 Offline</h1>
                    {Object.values(detectorData)
                        .filter(detector => !detector?.online)
                        .sort((a, b) => {
                            const aName = a.status.site_name || 'zzz';
                            const bName = b.status.site_name || 'zzz';
                            return aName.localeCompare(bName);
                        })
                        .map(detector => (
                            <div key={detector.mac} className="tool-card-wrapper">
                                <div className="initial-tool-card2" onClick={() => displayNotification(false, 'Offline detectors cannot make requests.')}>
                                    <div className="tool-card-overlay2"></div>
                                    <div className='initial-tool-card-content'>
                                        <h1 style={{ marginBottom: "0rem", marginLeft: '1rem' }}>
                                            {detector.status.site_name || detector.mac}
                                        </h1>
                                        <div className='each-tool'>
                                            <h1 style={{ marginBottom: "0rem" }}>Threshhold</h1>
                                            <h1 style={{ marginBottom: "0rem" }}>{detector.status.thr}</h1>
                                        </div>
                                        <div className='each-tool'>
                                            <h1 style={{ marginBottom: "0rem" }}>Used Memory</h1>
                                            <h1 style={{
                                                marginBottom: "0rem",
                                                color: getColorForMemory(Number(detector.status.memory), detector.online),
                                            }}>
                                                {detector.status.memory}
                                            </h1>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );

};

export default FnnToolView;
